/**
 * Traveler's AI Preferences — mirrors iOS TravelerPreferencesView.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Input,
  Modal,
  Select,
  Slider,
  message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { settingsEn } from '../../content/settings.en';
import {
  flushPreferencesPut,
  getUserPreferences,
  loadLocalUserPreferences,
  patchTravelerPreferenceProfile,
  type TravelerPreferenceProfile,
} from '../../services/userPreferencesSyncService';
import {
  ACCOMMODATION_BUDGET_STEPS,
  ACCOMMODATION_TYPES,
  DEFAULT_TRAVEL_PACE,
  DIETARY_PREFERENCES,
  SIGHTSEEING_STYLES,
  TIME_SLOTS,
  TRAVEL_PACES,
  TRAVEL_STYLE_TAGS,
  CUISINE_TYPES,
  accommodationBudgetIndex,
  normalizeAccommodationBudget,
  type CuisinePreferenceValue,
  isValidActivityInterestCount,
} from '../../utils/travelerPreferenceOptions';
import {
  activityInterestSelectionState,
  effectiveActivityInterestCount,
  loadViatorInterestGroups,
  toggleActivityInterestChild,
  toggleActivityInterestGroup,
  type ViatorInterestGroup,
} from '../../utils/viatorInterestTaxonomy';
import styles from './Settings.module.css';

const { TextArea } = Input;

const TravelerPreferences: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [loadingRemote, setLoadingRemote] = useState(true);
  const [profile, setProfile] = useState<TravelerPreferenceProfile>(
    () => loadLocalUserPreferences().travelerPreferenceProfile
  );
  const [activities, setActivities] = useState<Set<string>>(
    () => new Set(loadLocalUserPreferences().travelerPreferenceProfile.preferredActivities)
  );
  const [activityError, setActivityError] = useState<string | null>(null);
  const [sheetGroup, setSheetGroup] = useState<ViatorInterestGroup | null>(null);

  const groups = useMemo(() => loadViatorInterestGroups(), []);

  const applyProfile = useCallback((next: TravelerPreferenceProfile) => {
    setProfile(next);
    setActivities(new Set(next.preferredActivities ?? []));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/signin?redirect=/settings/traveler-preferences', { replace: true });
      return;
    }
    let cancelled = false;
    setLoadingRemote(true);
    void getUserPreferences()
      .then((payload) => {
        if (!cancelled) applyProfile(payload.travelerPreferenceProfile);
      })
      .catch(() => {
        if (!cancelled) {
          message.warning(settingsEn.loadError);
          applyProfile(loadLocalUserPreferences().travelerPreferenceProfile);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRemote(false);
      });
    return () => {
      cancelled = true;
      void flushPreferencesPut().catch(() => undefined);
    };
  }, [authLoading, isAuthenticated, navigate, applyProfile]);

  const persist = useCallback(
    (patch: Partial<TravelerPreferenceProfile>, nextActivities?: Set<string>) => {
      const preferredActivities = nextActivities
        ? Array.from(nextActivities)
        : Array.from(activities);
      const next = patchTravelerPreferenceProfile({
        ...patch,
        preferredActivities,
      });
      applyProfile(next.travelerPreferenceProfile);
    },
    [activities, applyProfile]
  );

  const handleSave = (): void => {
    const count = effectiveActivityInterestCount(activities, groups);
    if (!isValidActivityInterestCount(count)) {
      const msg =
        count < 3
          ? settingsEn.travelerPrefs.activityValidationMin
          : settingsEn.travelerPrefs.activityValidationMax;
      setActivityError(msg);
      message.error(msg);
      return;
    }
    setActivityError(null);
    persist({
      preferredAccommodationType: profile.preferredAccommodationType,
      averageHotelBudget: profile.averageHotelBudget,
      dietaryPreferences: profile.dietaryPreferences,
      travelPace: profile.travelPace,
      typicalStayDuration: profile.typicalStayDuration,
      preferredTimeSlots: profile.preferredTimeSlots,
      additionalPreferences: profile.additionalPreferences,
      cuisinePreferences: profile.cuisinePreferences,
      travelStyleTags: profile.travelStyleTags,
      sightseeingStyle: profile.sightseeingStyle,
    });
    message.success(settingsEn.travelerPrefs.saveSuccess);
  };

  const budget = normalizeAccommodationBudget(profile.averageHotelBudget);
  const stayNights = Math.min(7, Math.max(1, profile.typicalStayDuration ?? 3));
  const activityCount = effectiveActivityInterestCount(activities, groups);

  const toggleTag = (tag: string, list: string[] | null | undefined): string[] => {
    const set = new Set(list ?? []);
    if (set.has(tag)) set.delete(tag);
    else set.add(tag);
    return Array.from(set);
  };

  const setCuisine = (cuisine: string, value: CuisinePreferenceValue): void => {
    persist({
      cuisinePreferences: {
        ...(profile.cuisinePreferences ?? {}),
        [cuisine]: value,
      },
    });
  };

  if (authLoading || (!isAuthenticated && loadingRemote)) {
    return (
      <div className={styles.settings}>
        <div className={styles.container}>
          <p className={styles.muted}>{settingsEn.travelerPrefs.title}…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settings}>
      <div className={styles.container}>
        <header className={styles.header}>
          <Button
            type="link"
            className={styles.backLink}
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/settings')}
          >
            {settingsEn.back}
          </Button>
          <h1 className={styles.title}>{settingsEn.travelerPrefs.title}</h1>
          <p className={styles.hint}>{settingsEn.travelerPrefs.subtitle}</p>
        </header>

        {(profile.extractedPatterns?.length ?? 0) > 0 ? (
          <section className={styles.section} aria-labelledby="ai-patterns">
            <h2 id="ai-patterns" className={styles.sectionTitle}>
              {settingsEn.travelerPrefs.extractedPatterns}
            </h2>
            <ul className={styles.patternList}>
              {profile.extractedPatterns.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className={styles.section} aria-labelledby="ai-style">
          <h2 id="ai-style" className={styles.sectionTitle}>
            {settingsEn.travelerPrefs.travelStyle}
          </h2>
          <div className={styles.chipRow} role="group" aria-label={settingsEn.travelerPrefs.travelStyle}>
            {TRAVEL_PACES.map((pace) => {
              const label =
                pace === 'relaxed'
                  ? settingsEn.travelerPrefs.paceRelaxed
                  : pace === 'fast-paced'
                    ? settingsEn.travelerPrefs.paceFast
                    : settingsEn.travelerPrefs.paceModerate;
              const selected = (profile.travelPace || DEFAULT_TRAVEL_PACE) === pace;
              return (
                <button
                  key={pace}
                  type="button"
                  className={`${styles.chip} ${selected ? styles.chipSelected : ''}`}
                  aria-pressed={selected}
                  disabled={loadingRemote}
                  onClick={() => persist({ travelPace: pace })}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className={styles.fieldBlock}>
            <div className={styles.label}>{settingsEn.travelerPrefs.travelStyleTags}</div>
            <p className={styles.hint}>{settingsEn.travelerPrefs.travelStyleTagsHint}</p>
            <div className={styles.chipRow}>
              {TRAVEL_STYLE_TAGS.map((tag) => {
                const selected = (profile.travelStyleTags ?? []).includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    className={`${styles.chip} ${selected ? styles.chipSelected : ''}`}
                    aria-pressed={selected}
                    disabled={loadingRemote}
                    onClick={() =>
                      persist({ travelStyleTags: toggleTag(tag, profile.travelStyleTags) })
                    }
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.fieldBlock}>
            <div className={styles.label}>{settingsEn.travelerPrefs.preferredTimeSlots}</div>
            <div className={styles.chipRow}>
              {TIME_SLOTS.map((slot) => {
                const selected = (profile.preferredTimeSlots ?? []).includes(slot);
                return (
                  <button
                    key={slot}
                    type="button"
                    className={`${styles.chip} ${selected ? styles.chipSelected : ''}`}
                    aria-pressed={selected}
                    disabled={loadingRemote}
                    onClick={() =>
                      persist({
                        preferredTimeSlots: toggleTag(slot, profile.preferredTimeSlots),
                      })
                    }
                  >
                    {slot.charAt(0).toUpperCase() + slot.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.fieldBlock}>
            <div className={styles.label}>
              {settingsEn.travelerPrefs.typicalStayDuration(stayNights)}
            </div>
            <Slider
              min={1}
              max={7}
              step={1}
              value={stayNights}
              disabled={loadingRemote}
              onChange={(v) => persist({ typicalStayDuration: Number(v) })}
              aria-label={settingsEn.travelerPrefs.typicalStayDuration(stayNights)}
            />
          </div>

          <div className={styles.fieldBlock}>
            <div className={styles.label}>{settingsEn.travelerPrefs.additionalPreferences}</div>
            <TextArea
              rows={4}
              value={profile.additionalPreferences ?? ''}
              disabled={loadingRemote}
              placeholder={settingsEn.travelerPrefs.additionalPlaceholder}
              onChange={(e) => {
                const value = e.target.value;
                setProfile((prev) => ({ ...prev, additionalPreferences: value }));
              }}
              onBlur={(e) => {
                const trimmed = e.target.value.trim();
                persist({ additionalPreferences: trimmed || null });
              }}
              aria-label={settingsEn.travelerPrefs.additionalPreferences}
            />
          </div>
        </section>

        <section className={styles.section} aria-labelledby="your-prefs">
          <h2 id="your-prefs" className={styles.sectionTitle}>
            {settingsEn.travelerPrefs.yourPreferences}
          </h2>

          <div className={styles.fieldBlock}>
            <div className={styles.label}>{settingsEn.travelerPrefs.accommodationType}</div>
            <Select
              className={styles.fullControl}
              allowClear
              disabled={loadingRemote}
              placeholder={settingsEn.travelerPrefs.accommodationPlaceholder}
              value={profile.preferredAccommodationType ?? undefined}
              onChange={(value) =>
                persist({ preferredAccommodationType: value ? String(value) : null })
              }
              options={ACCOMMODATION_TYPES.map((t) => ({
                value: t,
                label: t.charAt(0).toUpperCase() + t.slice(1),
              }))}
              aria-label={settingsEn.travelerPrefs.accommodationType}
            />
          </div>

          <div className={styles.fieldBlock}>
            <div className={styles.label}>
              {settingsEn.travelerPrefs.accommodationBudget(budget)}
            </div>
            <Slider
              min={0}
              max={ACCOMMODATION_BUDGET_STEPS.length - 1}
              step={1}
              value={accommodationBudgetIndex(budget)}
              disabled={loadingRemote}
              tooltip={{ formatter: (idx) => `$${ACCOMMODATION_BUDGET_STEPS[Number(idx) || 0]}` }}
              onChange={(idx) =>
                persist({
                  averageHotelBudget: ACCOMMODATION_BUDGET_STEPS[Number(idx)] ?? budget,
                })
              }
              aria-label={settingsEn.travelerPrefs.accommodationBudget(budget)}
            />
          </div>

          <div className={styles.fieldBlock}>
            <div className={styles.label}>{settingsEn.travelerPrefs.sightseeingStyle}</div>
            <Select
              className={styles.fullControl}
              disabled={loadingRemote}
              value={profile.sightseeingStyle || 'no_preference'}
              onChange={(value) => persist({ sightseeingStyle: value })}
              options={SIGHTSEEING_STYLES.map((s) => ({
                value: s.value,
                label: settingsEn.travelerPrefs[s.labelKey],
              }))}
              aria-label={settingsEn.travelerPrefs.sightseeingStyle}
            />
          </div>

          <div className={styles.fieldBlock}>
            <div className={styles.activityHeader}>
              <div>
                <div className={styles.label}>{settingsEn.travelerPrefs.activityInterests}</div>
                <p className={styles.hint}>{settingsEn.travelerPrefs.activityInterestsHint}</p>
              </div>
              <span className={styles.badge}>
                {settingsEn.travelerPrefs.activitySelectedCount(activityCount)}
              </span>
            </div>
            {activityError ? <p className={styles.errorText}>{activityError}</p> : null}
            <div className={styles.interestGrid}>
              {groups.map((group) => {
                const state = activityInterestSelectionState(group, activities);
                const selected = state === 'allIncluded' || typeof state === 'object';
                return (
                  <div
                    key={group.id}
                    className={`${styles.interestCard} ${selected ? styles.interestCardSelected : ''}`}
                  >
                    <button
                      type="button"
                      className={styles.interestCardMain}
                      disabled={loadingRemote}
                      aria-pressed={state === 'allIncluded'}
                      onClick={() => {
                        const next = toggleActivityInterestGroup(group, activities);
                        setActivities(next);
                        setActivityError(null);
                        persist({}, next);
                      }}
                    >
                      <strong>{group.name}</strong>
                      {typeof state === 'object' ? (
                        <span className={styles.muted}>{state.partial} selected</span>
                      ) : null}
                    </button>
                    <Button
                      size="small"
                      type="link"
                      disabled={loadingRemote}
                      onClick={() => setSheetGroup(group)}
                    >
                      {settingsEn.travelerPrefs.activityPickItems}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.fieldBlock}>
            <div className={styles.label}>{settingsEn.travelerPrefs.dietaryPreferences}</div>
            <div className={styles.chipRow}>
              {DIETARY_PREFERENCES.map((item) => {
                const selected = (profile.dietaryPreferences ?? []).includes(item);
                return (
                  <button
                    key={item}
                    type="button"
                    className={`${styles.chip} ${selected ? styles.chipSelected : ''}`}
                    aria-pressed={selected}
                    disabled={loadingRemote}
                    onClick={() =>
                      persist({
                        dietaryPreferences: toggleTag(item, profile.dietaryPreferences),
                      })
                    }
                  >
                    {item}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.fieldBlock}>
            <div className={styles.label}>{settingsEn.travelerPrefs.cuisinePreferences}</div>
            <div className={styles.cuisineList}>
              {CUISINE_TYPES.map((cuisine) => {
                const value = (profile.cuisinePreferences?.[cuisine] ||
                  'like') as CuisinePreferenceValue;
                return (
                  <div key={cuisine} className={styles.cuisineRow}>
                    <span className={styles.cuisineName}>{cuisine}</span>
                    <div className={styles.cuisineToggles} role="group" aria-label={cuisine}>
                      {(
                        [
                          ['love', settingsEn.travelerPrefs.cuisineLove],
                          ['like', settingsEn.travelerPrefs.cuisineLike],
                          ['avoid', settingsEn.travelerPrefs.cuisineAvoid],
                        ] as const
                      ).map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          className={`${styles.cuisineBtn} ${value === key ? styles.cuisineBtnSelected : ''} ${
                            key === 'love'
                              ? styles.cuisineLove
                              : key === 'avoid'
                                ? styles.cuisineAvoid
                                : styles.cuisineLike
                          }`}
                          aria-pressed={value === key}
                          disabled={loadingRemote}
                          onClick={() => setCuisine(cuisine, key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <div className={styles.actions}>
          <Button type="primary" size="large" disabled={loadingRemote} onClick={handleSave}>
            {settingsEn.save}
          </Button>
          {profile.lastUpdated ? (
            <p className={styles.muted}>{settingsEn.lastUpdated(profile.lastUpdated)}</p>
          ) : null}
        </div>
      </div>

      <Modal
        open={sheetGroup != null}
        title={sheetGroup?.name}
        onCancel={() => setSheetGroup(null)}
        footer={null}
        destroyOnClose
      >
        {sheetGroup ? (
          <div className={styles.chipRow}>
            {sheetGroup.children.map((child) => {
              const selected =
                activities.has(child.id) || activities.has(sheetGroup.id);
              return (
                <button
                  key={child.id}
                  type="button"
                  className={`${styles.chip} ${selected ? styles.chipSelected : ''}`}
                  aria-pressed={activities.has(child.id) || activities.has(sheetGroup.id)}
                  onClick={() => {
                    const next = toggleActivityInterestChild(
                      child.id,
                      sheetGroup,
                      activities
                    );
                    setActivities(next);
                    setActivityError(null);
                    persist({}, next);
                  }}
                >
                  {child.name}
                </button>
              );
            })}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default TravelerPreferences;
